'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { FiRefreshCw } from "react-icons/fi";
interface FormData {
  name: string;
  email: string;
  message: string;
}

interface CaptchaChallenge {
  question: string;
  answer: number;
}

interface FieldErrors {
  name?: string;
  email?: string;
  message?: string;
}

import { GrLocation } from "react-icons/gr";
import { FaPhone } from "react-icons/fa6";

import NewsletterSection from '@/components/home-page/body/Newslettersection';
import dynamic from 'next/dynamic';

const LiveLocationMap = dynamic(() => import('@/components/LiveLocationMap'), {
  ssr: false,
});


const ContactForm: React.FC = () => {
    const t = useTranslations('Search');
    const te = useTranslations('BrandingStory');
    const ts = useTranslations('Search');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: '',
  });

  const [captcha, setCaptcha] = useState<CaptchaChallenge>({ question: '', answer: 0 });
  const [captchaInput, setCaptchaInput] = useState<string>('');
  const [captchaValid, setCaptchaValid] = useState<boolean>(false);
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | '', message: string }>({
    type: '',
    message: '',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Generate advanced CAPTCHA
  const generateCaptcha = () => {
    const operations = [
      { op: '+', fn: (a: number, b: number) => a + b },
      { op: '-', fn: (a: number, b: number) => a - b },
      { op: '×', fn: (a: number, b: number) => a * b },
    ];

    const num1 = Math.floor(Math.random() * 20) + 1;
    const num2 = Math.floor(Math.random() * 20) + 1;
    const selectedOp = operations[Math.floor(Math.random() * operations.length)];

    const answer = selectedOp.fn(num1, num2);
    const question = `${num1} ${selectedOp.op} ${num2} = ?`;

    setCaptcha({ question, answer });
    setCaptchaInput('');
    setCaptchaValid(false);
  };

  // Validate CAPTCHA
  const validateCaptcha = (value: string) => {
    const isValid = parseInt(value) === captcha.answer;
    setCaptchaValid(isValid);
    return isValid;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear the field's error as soon as the user starts fixing it
    if (fieldErrors[name as keyof FieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCaptchaInput(value);
    validateCaptcha(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    // Validate each field individually so we can show specific messages
    const errors: FieldErrors = {};
    if (!formData.name.trim()) errors.name = t('Namer') || 'Name is required.';
    if (!formData.email.trim()) errors.email = t('Emailr') || 'Email is required.';
    if (!formData.message.trim()) errors.message = t('Messager') || 'Message is required.';

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setStatus({ type: 'error', message: t('fields')  });
      return;
    }

    // Show CAPTCHA if not shown yet
    if (!showCaptcha) {
      setShowCaptcha(true);
      generateCaptcha();
      setStatus({ type: '', message: t('complete') });
      return;
    }

    if (!captchaValid) {
      setStatus({ type: 'error', message: t('solving') });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: t('sent') });
        setFormData({ name: '', email: '', message: '' });
        setShowCaptcha(false);
        setCaptchaInput('');
        setCaptchaValid(false);
        setFieldErrors({});
      } else {
        setStatus({ type: 'error', message: data.error || t('Failed') });
      }
    } catch (error) {
      setStatus({ type: 'error', message: t('error') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div >
<div
  className=" bg-yel  "
>

<LiveLocationMap />

<div className='grid grid-cols-1 lg:grid-cols-2 gap-8 px-4 md:px-6 '>

  <div >
    <h1 className='uppercase font-semibold text-4xl mb-8 xl:mb-16'>{ts('touch')}</h1>

<p className='text-b mb-8'>{ts('des1')} <br />
{ts('des2')} <br />
{ts('des3')}
</p>

    <div className='flex gap-2 items-center  text-b'>
        <GrLocation size={24}/>
    <p> {ts('address')} </p>
    </div>
  <p> {ts('address1')} </p>
<div className='flex gap-2 items-center text-b'>
  <FaPhone/>
  <p dir='ltr'>0777 79 30 19</p>
</div>

  </div>

        <form onSubmit={handleSubmit} className="space-y-6 py-16">
          <div className='flex gap-4 w-full'>
            <div className='w-full'>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full border-b bg-transparent pb-3 text-base tracking-wide text-b placeholder-b outline-none transition-colors focus:border-white disabled:opacity-50 ${
                  fieldErrors.name ? 'border-red-500' : 'border-black'
                }`}
                placeholder={t('Name')}
              />
              {fieldErrors.name && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div className='w-full'>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full border-b bg-transparent pb-3 text-base tracking-wide text-b placeholder-b outline-none transition-colors focus:border-white disabled:opacity-50 ${
                  fieldErrors.email ? 'border-red-500' : 'border-black'
                }`}
                placeholder={t('Email')}
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.email}</p>
              )}
            </div>
          </div>

          {/* Message Field */}
          <div>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={5}
              className={`w-full border-b bg-transparent pb-3 text-base tracking-wide text-b placeholder-b outline-none transition-colors focus:border-white disabled:opacity-50 resize-none ${
                fieldErrors.message ? 'border-red-500' : 'border-black'
              }`}
              placeholder={t('Message')}
            />
            {fieldErrors.message && (
              <p className="text-red-500 text-sm mt-1">{fieldErrors.message}</p>
            )}
          </div>

          {/* General status message (e.g. captcha prompts, success, server errors) */}
          {status.message && (
            <p
              className={`text-sm ${
                status.type === 'error'
                  ? 'text-red-500'
                  : status.type === 'success'
                  ? 'text-green-600'
                  : 'text-b'
              }`}
            >
              {status.message}
            </p>
          )}

          {/* Advanced CAPTCHA - Only shows after first submit */}
          {showCaptcha && (
            <div className=" animate-fadeIn items-center flex flex-col ">


              <div className="bg-yel p-1 w-60 rounded-lg shadow-sm mb-4 flex justify-around items-center">
                <p className="text-2xl font-bold text-center text-gray-900 font-mono tracking-wider">
                  {captcha.question}
                </p>
                 <FiRefreshCw size={24}  onClick={() => {
                    generateCaptcha();
                    setCaptchaInput('');
                  }}/>
              </div>

              <div className="relative">
                <input
                  type="number"
                  value={captchaInput}
                  onChange={handleCaptchaChange}
                  className={`px-3 w-60 py-2 border-1 rounded-lg outline-none transition-all text-gray-900 ${
                    captchaInput === ''
                      ? 'border-gray-300 focus:border-yel'
                      : captchaValid
                      ? 'border-green-500 bg-green-50'
                      : 'border-red-500 bg-red-50'
                  }`}
                  placeholder={t('answer')}
                  required
                />

              </div>
            </div>
          )}



          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || (showCaptcha && !captchaValid)}
            className="w-full bg-secondary text-yel py-2 rounded-lg hover:bg-yel font-medium hover:text-black transition-colors uppercase"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('sending')}
              </span>
            ) : showCaptcha && !captchaValid ? (
              t('solve')
            ) : (
              t('send')
            )}
          </button>
        </form>


        </div>
      </div>


      <NewsletterSection
        logoSrc="/chohra-white.png"
        logoAlt="chohra"
        tagline={te('join')}
      />
    </div>
  );
};

export default ContactForm;